import React, { useEffect } from 'react';
import { Button, Typography } from 'antd';
import { StarOutlined } from '@ant-design/icons';
import AdBanner from '../../components/AdBanner';
import { useTrackLPView, trackLPClick } from '../../utils/lpTracking';
import futezapLogo from '../../assets/futezap.jpg';
import shotLogin from '../../assets/landing/player-login.png';
import shotRatingModal from '../../assets/landing/rating-modal.png';
import shotReputation from '../../assets/landing/player-reputation.png';
import shotStats from '../../assets/landing/player-stats.png';
import shotGames from '../../assets/landing/player-games.png';

const { Title, Text, Paragraph } = Typography;

const LP_PATH = '/como-avaliar';

function setMeta(name, content, attr = 'name') {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

const HowRatingWorks = () => {
  useTrackLPView(LP_PATH);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Novidade: avaliar a galera — Futezap';
    setMeta('description', 'Como funciona a nova função de avaliação de jogadores no Futezap: quem tá jogando, como avaliar depois do jogo, e como as notas ficam.');
    setMeta('og:title', 'Novidade no Futezap: avaliação da galera', 'property');
    setMeta('og:description', 'Depois do jogo, dá 1 a 5 estrelas pros jogadores. Anônimo, só a média aparece.', 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('og:image', `${window.location.origin}/futezap.jpg`, 'property');
    return () => { document.title = prev; };
  }, []);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const openApp = () => {
    trackLPClick(LP_PATH, 'ir_pra_central');
    window.location.href = 'https://admin.futezap.com.br/player/login';
  };

  const shotStyle = {
    width: '100%',
    maxWidth: 280,
    borderRadius: 14,
    boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
    border: '1px solid #eee',
    display: 'block',
  };

  const stepWithShot = ({ n, titulo, children, img, imgAlt, imgRight = true }) => (
    <div style={{
      display: 'flex', flexDirection: isMobile ? 'column' : (imgRight ? 'row' : 'row-reverse'),
      gap: isMobile ? 16 : 32, alignItems: 'flex-start',
      padding: '28px 0', borderTop: '1px solid #eef1f4',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>{n}</Text>
        <Title level={4} style={{ margin: '4px 0 8px' }}>{titulo}</Title>
        <div style={{ color: '#4a4a4a', fontSize: 15, lineHeight: 1.6 }}>{children}</div>
      </div>
      {img && (
        <div style={{ flex: '0 0 auto', alignSelf: isMobile ? 'center' : 'flex-start' }}>
          <img src={img} alt={imgAlt} style={shotStyle} loading="lazy" />
        </div>
      )}
    </div>
  );

  const stepPlain = ({ n, titulo, children }) => (
    <div style={{ padding: '28px 0', borderTop: '1px solid #eef1f4' }}>
      <Text style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>{n}</Text>
      <Title level={4} style={{ margin: '4px 0 8px' }}>{titulo}</Title>
      <div style={{ color: '#4a4a4a', fontSize: 15, lineHeight: 1.6 }}>{children}</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      <AdBanner publicMode />
      <div style={{
        maxWidth: 900, margin: '0 auto',
        padding: isMobile ? '20px 12px 40px' : '32px 20px 64px',
      }}>

        {/* Cabecalho */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <img src={futezapLogo} alt="Futezap" style={{ height: 28, width: 28, borderRadius: 5 }} />
          <Text strong style={{ color: '#128C7E', fontSize: 15 }}>Futezap</Text>
          <span style={{ color: '#c1c9d2', fontSize: 13 }}>·</span>
          <Text style={{ color: '#8c8c8c', fontSize: 13 }}>Novidades</Text>
        </div>

        {/* Post */}
        <div style={{
          background: '#fff', borderRadius: 14,
          padding: isMobile ? '24px 20px' : '40px 44px',
          border: '1px solid #eef1f4',
        }}>
          <Text style={{ color: '#8c8c8c', fontSize: 13 }}>Agosto de 2026</Text>
          <Title level={2} style={{ marginTop: 6, marginBottom: 16 }}>
            Agora você avalia a galera ⭐
          </Title>

          <Paragraph style={{ color: '#4a4a4a', fontSize: 16, lineHeight: 1.65 }}>
            Fala, boleiro. Liberamos essa semana a <strong>avaliação de jogadores</strong> aqui
            no Futezap. Depois do jogo, você dá de 1 a 5 estrelas pra cada boleiro que jogou
            com você. Simples assim.
          </Paragraph>

          <Paragraph style={{ color: '#4a4a4a', fontSize: 16, lineHeight: 1.65, marginBottom: 24 }}>
            E o mais importante: é <strong>anônimo</strong>. Ninguém sabe quem deu qual nota.
            Só a média aparece.
          </Paragraph>

          {stepPlain({
            n: 'PASSO 1',
            titulo: 'Confirma presença como você já faz',
            children: (
              <>
                Continua igual: chega a convocação do time no grupo, você aperta pra confirmar.
                Nada muda aqui.
              </>
            ),
          })}

          {stepPlain({
            n: 'PASSO 2',
            titulo: 'Depois do jogo, o Futezap manda o link no grupo',
            children: (
              <>
                Umas horas depois da pelada, cai uma mensagem no <strong>grupo do time</strong>{' '}
                com um link. Toca nele.
              </>
            ),
          })}

          {stepWithShot({
            n: 'PASSO 3',
            titulo: 'Se for a primeira vez, faz login rápido',
            img: shotLogin,
            imgAlt: 'Tela de login do jogador',
            imgRight: true,
            children: (
              <>
                Endereço da sua central: <a href="https://admin.futezap.com.br/player/login"
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => trackLPClick(LP_PATH, 'inline_link_login')}>
                  admin.futezap.com.br/player/login</a>
                <br /><br />
                Se você já tem conta, entra direto com Google ou WhatsApp+senha.
                <br /><br />
                Se é a primeira vez, o cadastro leva 1 minuto — só o WhatsApp que o organizador
                usou pra te convocar. A gente faz o resto.
              </>
            ),
          })}

          {stepWithShot({
            n: 'PASSO 4',
            titulo: 'Vai em "Jogos" e clica em Avaliar no jogo elegível',
            img: shotGames,
            imgAlt: 'Aba Jogos do jogador',
            imgRight: false,
            children: (
              <>
                Na sua central, aba <strong>Jogos</strong>, cada partida que você jogou
                mostra um botão <strong>Avaliar</strong> enquanto a janela estiver aberta.
                Se aparecer <em>"Prazo encerrado"</em>, você já não avalia mais aquele jogo.
                <br /><br />
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ⚠️ A janela é de <strong>24 horas</strong> após o jogo (começa 2h depois do
                  fim da partida). Depois disso, não dá mais pra avaliar.
                </Text>
                <br /><br />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Dica: na aba <strong>Estatísticas</strong> tem um card laranja de{' '}
                  <strong>"Avaliações pendentes"</strong> com o botão de avaliar direto —
                  atalho pra não precisar caçar o jogo na lista.
                </Text>
              </>
            ),
          })}

          {stepWithShot({
            n: 'PASSO 5',
            titulo: 'Toca em cada jogador e dá as estrelinhas',
            img: shotRatingModal,
            imgAlt: 'Modal de avaliação de jogadores',
            imgRight: true,
            children: (
              <>
                Abre um modal com todos os jogadores que confirmaram presença nesse jogo.
                Você toca em cada um e escolhe de <strong>1 a 5 estrelas</strong>. Leva uns
                30 segundos.
                <br /><br />
                Aperta <strong>Salvar</strong> e pronto.
                <br /><br />
                <Text type="secondary" style={{ fontSize: 14 }}>
                  Só quem confirmou presença e jogou aparece na lista. Você não avalia quem
                  não jogou.
                </Text>
              </>
            ),
          })}

          {stepPlain({
            n: 'IMPORTANTE',
            titulo: 'É 100% anônimo',
            children: (
              <>
                Ninguém — nem o organizador, nem outros jogadores — sabe quem deu qual nota.
                O sistema só mostra a <strong>média das avaliações</strong>. Fica leve — vota
                sincero sem medo de confusão.
              </>
            ),
          })}

          {stepWithShot({
            n: 'BÔNUS',
            titulo: 'Você também recebe as suas notas',
            img: shotReputation,
            imgAlt: 'Reputação do jogador com estrelas',
            imgRight: true,
            children: (
              <>
                Na sua central do Futezap você vê sua média com quantas avaliações recebeu,
                além de gols, assist, cartões, taxa de presença — tudo do seu boleiro no time.
                <br /><br />
                Não vira competição — mas dá pra saber como a galera tá te avaliando.
              </>
            ),
          })}

          {stepWithShot({
            n: 'ONDE VER',
            titulo: 'Sua central: estatísticas + jogos + frequência',
            img: shotStats,
            imgAlt: 'Home logado do jogador com estatísticas',
            imgRight: false,
            children: (
              <>
                Depois de logar, você tem 3 abas: <strong>Estatísticas</strong> (números do time,
                sua reputação), <strong>Jogos</strong> (seu histórico) e <strong>Minha frequência</strong>{' '}
                (taxa de confirmação e presença).
              </>
            ),
          })}

          {/* Rodape */}
          <div style={{ borderTop: '1px solid #eef1f4', paddingTop: 24, marginTop: 32 }}>
            <Paragraph style={{ color: '#4a4a4a', fontSize: 15, lineHeight: 1.65 }}>
              Se seu time ainda não usa Futezap, chama o organizador pra dar uma olhada.
              A avaliação e todo o resto (convocação automática, ranking, cobrança) rodam
              lá no zap sem instalar nada.
            </Paragraph>
            <Paragraph style={{ color: '#8c8c8c', fontSize: 14, marginBottom: 20 }}>
              Qualquer dúvida, chama a gente no mesmo zap que manda as convocações.
            </Paragraph>

            <Button
              type="primary"
              icon={<StarOutlined />}
              onClick={openApp}
              style={{
                background: '#128C7E', borderColor: '#128C7E',
                fontWeight: 600, height: 40, padding: '0 20px',
              }}
            >
              Ir pra minha central
            </Button>
          </div>
        </div>

        {/* Assinatura */}
        <div style={{ textAlign: 'center', color: '#8c8c8c', fontSize: 13, marginTop: 20 }}>
          — time Futezap
        </div>
      </div>
    </div>
  );
};

export default HowRatingWorks;
